import React, {useContext, useEffect, useRef, useState} from "react";
import ErrorModal from "../components/ErrorModal";
import SuccessModal from "../components/SuccessModal";
import ChooseSolutionV2 from "../components/ChooseSolutionV2";
import axiosInstance from "../utils/axiosInstance";
import {ArrowUpTrayIcon} from "@heroicons/react/24/outline";
import ErrorAlert from "../components/ErrorAlert";
import {useNavigate} from "react-router-dom";
import {AuthContext} from "../context/AuthContext";
import ProgressBar from "../components/ProgressBar";
import {
    Alert,
    Page,
    PageHeader,
    PageLoader,
    Panel,
    Spinner,
    buttonClass,
} from "../components/ui";

const FileUpload = () => {
    const {updateUserLocal} = useContext(AuthContext);
    const [file, setFile] = useState(null);
    const [formEnabled, setFormEnabled] = useState(false);
    const [selectedOptions, setSelectedOptions] = useState([]);
    const [selectedSolutionIds, setSelectedSolutionIds] = useState([]);
    const [selected, setSelected] = useState({TUNING: null, OPTION: []});
    const [showModal, setShowModal] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [solutionErrorMessage, setSolutionErrorMessage] = useState("");
    const [successModal, setSuccessModal] = useState(false);
    const [errorModal, setErrorModal] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [totalCredits, setTotalCredits] = useState(0);
    const [solutions, setSolutions] = useState([]);
    const prevSolutions = useRef([]);
    const [limitExceeded, setLimitExceeded] = useState(false);
    const [uploaderText, setUploaderText] = useState(null);
    const [loading, setLoading] = useState(true);
    const [additionalPrices, setAdditionalPrices] = useState([]);
    const [extraPrice, setExtraPrice] = useState(0);

    const navigate = useNavigate();

    useEffect(() => {
        axiosInstance
            .get("/solutions")
            .then((response) => {
                setSolutions(response.data);
                prevSolutions.current = response.data;
            })
            .catch((error) => {
                console.error("Error fetching solutions:", error);
            });
    }, []);

    useEffect(() => {
        axiosInstance
            .get("/additional-prices")
            .then((response) => {
                setAdditionalPrices(response.data);
            })
            .catch((error) => {
                console.error("Error fetching solutions:", error);
            });
    }, []);

    useEffect(() => {
        const fetchUploaderText = async () => {
            try {
                const response = await axiosInstance.get("/application-configuration");
                const uploaderText = response.data.uploader_text;
                if (uploaderText !== null && uploaderText !== "") {
                    setUploaderText(uploaderText);
                }
            } catch (error) {
                console.error("Failed to fetch banner text", error);
            } finally {
                setLoading(false);
            }
        };
        fetchUploaderText();
    }, []);

    const updateSolutionPricesByAdditionalPrices = (e) => {
        const {value} = e.target;
        const normalizedValue = value.toLowerCase().trim();

        const matchedRule = additionalPrices.find((rule) => {
            const ruleValue = rule.value.toLowerCase();
            switch (rule.rule) {
                case 'CONTAINS':
                    return normalizedValue.includes(ruleValue);
                case 'EXACT_MATCH':
                    return ruleValue === normalizedValue;
                case 'STARTS_WITH':
                    return normalizedValue.startsWith(ruleValue);
                default:
                    return false;
            }
        });

        const creditsToAdd = matchedRule ? matchedRule.credits : 0;
        setExtraPrice(creditsToAdd)
        const updatedSolutions = prevSolutions.current.map((solution) => ({
            ...solution,
            credits: solution.credits + creditsToAdd,
        }));

        setSolutions(updatedSolutions);
    };

    const handleFileChange = (event) => {
        const selectedFile = event.target.files[0];
        if (selectedFile && selectedFile.size <= 20 * 1024 * 1024) {
            setFile(selectedFile);
            setFormEnabled(true);
            setErrorMessage("");
        } else {
            setErrorMessage("File size should not exceed 20MB.");
        }
    };

    const handleDrop = (event) => {
        event.preventDefault();
        const droppedFile = event.dataTransfer.files[0];
        if (droppedFile && droppedFile.size <= 20 * 1024 * 1024) {
            setFile(droppedFile);
            setFormEnabled(true);
            setErrorMessage("");
        } else {
            setErrorMessage("File size should not exceed 20MB.");
        }
    };

    const uploadFileWithRetry = async (url, data, options, maxRetries = 3) => {
        let retries = 0;
        while (retries <= maxRetries) {
            try {
                return await axiosInstance.post(url, data, options);
            } catch (error) {
                retries++;
                if (retries > maxRetries || !error.isAxiosError || error.response) {
                    throw error;
                }
            }
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!file) {
            alert("Please select a file first.");
            return;
        }

        setProgress(0);

        const jsonData = {
            vehicle: document.getElementById("type").value,
            brand: document.getElementById("brand").value,
            model: document.getElementById("model").value,
            ecu: document.getElementById("ecu").value,
            petrol_type: document.getElementById("petrol_type").value,
            generation: document.getElementById("generation").value,
            gear: document.getElementById("gear").value,
            reading_device: document.getElementById("reading_device").value,
            description: document.getElementById("description").value,
            solution: selectedOptions.join(", "),
            solution_ids: [
                ...(selected.TUNING ? [selected.TUNING] : []),
                ...selected.OPTION,
            ],
            price: 0,
        };

        if (jsonData.solution.trim() === "") {
            setSolutionErrorMessage("⬆️ You must select at least one solution to upload the file. ⬆️");
            return;
        }

        setIsLoading(true);
        const formData = new FormData();
        formData.append("file", file);
        formData.append("jsonData", JSON.stringify(jsonData));

        try {
            await uploadFileWithRetry(
                "/files",
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                    onUploadProgress: (progressEvent) => {
                        let percentCompleted = Math.round(
                            (progressEvent.loaded * 100) / progressEvent.total
                        );

                        if (progressEvent.loaded === progressEvent.total) {
                            percentCompleted = 100;
                        }

                        setProgress(percentCompleted);
                    },
                },
                3
            );

            setSuccessModal(true);
            setTimeout(() => {
                setSuccessModal(false);
                navigate("/my-files");
            }, 3000);

            updateUserLocal();

            setFile(null);
            setFormEnabled(false);
            setSelectedOptions([]);
            setErrorMessage("");
            setProgress(0);
            setTotalCredits(0);
            setSelectedSolutionIds([]);
            setSelected({TUNING: null, OPTION: []});

            event.target.reset();
        } catch (error) {
            if (error.response?.status === 400) {
                setErrorMessage(error.response.data.message);
                return;
            }
            setErrorModal(true);
            setTimeout(() => setErrorModal(false), 3000);
        } finally {
            setIsLoading(false);
        }
    };

    if (loading) {
        return (
            <Page width="max-w-5xl">
                <PageHeader title="Upload File" onBack={() => window.history.back()}/>
                <PageLoader/>
            </Page>
        );
    }

    // A configured uploader notice replaces the form entirely — e.g. while
    // uploads are paused.
    if (uploaderText) {
        return (
            <Page width="max-w-5xl">
                <PageHeader title="Upload File" onBack={() => window.history.back()}/>
                <Alert tone="info">{uploaderText}</Alert>
            </Page>
        );
    }

    return (
        <Page width="max-w-5xl">
            <PageHeader
                title="Upload File"
                subtitle="Send us a read and pick the solutions you need."
                onBack={() => window.history.back()}
            />

            <Alert tone="info">
                After uploading, your file stays in the <strong>Dashboard</strong> until completed.
                Before completion you can open a ticket for changes, or cancel for an automatic
                refund. Once completed, cancellations and refunds are not possible.
                {extraPrice > 0 && (
                    <span className="mt-1 block font-semibold text-rose-600 dark:text-rose-400">
                        Note: prices may increase for specific ECU models by {extraPrice} CRS.
                    </span>
                )}
            </Alert>

            <Panel>
                <form onSubmit={handleSubmit}>
                    <label
                        htmlFor="fileInput"
                        className={`flex cursor-pointer flex-col items-center justify-center gap-2 rounded-[18px] border-2 border-dashed px-6 py-10 text-center transition ${
                            formEnabled
                                ? "border-emerald-500/60 bg-emerald-500/5 text-emerald-600 dark:text-emerald-400"
                                : "border-gray-300 bg-gray-50 text-gray-500 hover:border-red-400/60 dark:border-[#3a3a3d] dark:bg-[#161617] dark:text-slate-400"
                        }`}
                        onDrop={handleDrop}
                        onDragOver={(e) => e.preventDefault()}
                    >
                        <span
                            className={`flex h-14 w-14 items-center justify-center rounded-[16px] ${
                                formEnabled
                                    ? "bg-emerald-500/15"
                                    : "bg-gradient-to-br from-red-500/15 to-orange-500/15 text-red-600 dark:text-orange-400"
                            }`}
                        >
                            <ArrowUpTrayIcon className="h-7 w-7"/>
                        </span>
                        <p className="text-sm font-bold">
                            {formEnabled && file ? file.name : "Drag & drop your file here, or click to browse"}
                        </p>
                        <p className="text-xs text-gray-400 dark:text-slate-500">
                            {formEnabled ? "Click to choose a different file" : "Up to 20 MB"}
                        </p>

                        <input
                            type="file"
                            onChange={handleFileChange}
                            className="hidden"
                            id="fileInput"
                        />
                    </label>

                    {errorMessage && <ErrorAlert errorMessage={errorMessage}/>}

                    <div className="mt-6">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {/* Vehicle Select */}
                            <div>
                                <label
                                    htmlFor="type"
                                    className="block text-xs font-bold uppercase tracking-[0.08em] text-gray-500 dark:text-slate-400"
                                >
                                    Vehicle {" "}
                                </label>
                                <select
                                    id="type"
                                    name="type"
                                    className="mt-1.5 block h-11 w-full rounded-[12px] border border-gray-300 bg-white px-3 text-sm text-gray-800 outline-none transition focus:border-red-400 focus:ring-4 focus:ring-red-500/10 disabled:opacity-60 dark:border-[#3a3a3d] dark:bg-[#262628] dark:text-slate-100"
                                    disabled={!formEnabled}
                                    required
                                >
                                    <option value=""></option>
                                    <option value="Car">Car</option>
                                    <option value="Truck">Truck</option>
                                    <option value="Van">Van</option>
                                    <option value="Tracktor">Tracktor</option>
                                    <option value="Excavator">Excavator</option>
                                    <option value="Motorcycle/ATV">Motorcycle/ATV</option>

                                    {/* Add more vehicle options here */}
                                </select>
                            </div>

                            {/* Brand Select */}
                            <div>
                                <label
                                    htmlFor="brand"
                                    className="block text-xs font-bold uppercase tracking-[0.08em] text-gray-500 dark:text-slate-400"
                                >
                                    Brand
                                </label>
                                <select
                                    id="brand"
                                    name="brand"
                                    className="mt-1.5 block h-11 w-full rounded-[12px] border border-gray-300 bg-white px-3 text-sm text-gray-800 outline-none transition focus:border-red-400 focus:ring-4 focus:ring-red-500/10 disabled:opacity-60 dark:border-[#3a3a3d] dark:bg-[#262628] dark:text-slate-100"
                                    disabled={!formEnabled}
                                    required
                                >
                                    <option value=""></option>
                                    <option value="VW-Audi-Seat-Skoda">VW-Audi-Seat-Skoda</option>
                                    <option value="BMW">BMW</option>
                                    <option value="Mercedes-Benz">Mercedes-Benz</option>
                                    <option value="Opel">Opel</option>
                                    <option value="Fiat">Fiat</option>
                                    <option value="Ford">Ford</option>
                                    <option value="Peugeot">Peugeot</option>
                                    <option value="Renault">Renault</option>
                                    <option value="Citroen">Citroen</option>
                                    <option value="Volvo">Volvo</option>
                                    <option value="" disabled>
                                        -----------------------------------------------------------
                                    </option>
                                    <option value="Acura">Acura</option>
                                    <option value="AEBI SCHMIDT">AEBI SCHMIDT</option>
                                    <option value="AGCO">AGCO</option>
                                    <option value="Agrale">Agrale</option>
                                    <option value="Alexander Dennis">Alexander Dennis</option>
                                    <option value="Alfa Romeo">Alfa Romeo</option>
                                    <option value="Aprillia">Aprillia</option>
                                    <option value="Arbos">Arbos</option>
                                    <option value="Aston Martin">Aston Martin</option>
                                    <option value="Astra">Astra</option>
                                    <option value="Auspicious">Auspicious</option>
                                    <option value="Autobianchi">Autobianchi</option>
                                    <option value="Baojun">Baojun</option>
                                    <option value="Beijing">Beijing</option>
                                    <option value="Bentley">Bentley</option>
                                    <option value="Bmc">Bmc</option>
                                    <option value="BMW Moto">BMW Moto</option>
                                    <option value="Bobcat">Bobcat</option>
                                    <option value="Bouwmann">Bouwmann</option>
                                    <option value="Brilliance">Brilliance</option>
                                    <option value="Bugatti">Bugatti</option>
                                    <option value="Buick">Buick</option>
                                    <option value="Cadillac">Cadillac</option>
                                    <option value="CAMC">CAMC</option>
                                    <option value="Can-am">Can-am</option>
                                    <option value="Can-am Seadoo">Can-am Seadoo</option>
                                    <option value="Case">Case</option>
                                    <option value="Caterpillar">Caterpillar</option>
                                    <option value="CF MOTO">CF MOTO</option>
                                    <option value="Challenger">Challenger</option>
                                    <option value="Chery">Chery</option>
                                    <option value="Chevrolet">Chevrolet</option>
                                    <option value="Chrysler">Chrysler</option>
                                    <option value="Claas">Claas</option>
                                    <option value="Class Area">Class Area</option>
                                    <option value="Cummins">Cummins</option>
                                    <option value="Cupra">Cupra</option>
                                    <option value="Dacia">Dacia</option>
                                    <option value="Daewoo">Daewoo</option>
                                    <option value="DAF">DAF</option>
                                    <option value="Deutz">Deutz</option>
                                    <option value="Dodge">Dodge</option>
                                    <option value="Dongfeng">Dongfeng</option>
                                    <option value="Doosan">Doosan</option>
                                    <option value="DSG">DSG</option>
                                    <option value="Ducati">Ducati</option>
                                    <option value="Faresin">Faresin</option>
                                    <option value="FAW">FAW</option>
                                    <option value="Fendt">Fendt</option>
                                    <option value="Ferrari">Ferrari</option>
                                    <option value="Force">Force</option>
                                    <option value="Ford Trucks">Ford Trucks</option>
                                    <option value="Freightliner">Freightliner</option>
                                    <option value="Gamma">Gamma</option>
                                    <option value="Gaz">Gaz</option>
                                    <option value="Geely">Geely</option>
                                    <option value="GEHL">GEHL</option>
                                    <option value="Gelly">Gelly</option>
                                    <option value="Gillera">Gillera</option>
                                    <option value="Ginomoto">Ginomoto</option>
                                    <option value="GMC">GMC</option>
                                    <option value="Great Wall">Great Wall</option>
                                    <option value="Haima">Haima</option>
                                    <option value="Halcon">Halcon</option>
                                    <option value="Harley Davidson">Harley Davidson</option>
                                    <option value="Haval">Haval</option>
                                    <option value="Higer Bus">Higer Bus</option>
                                    <option value="Hino">Hino</option>
                                    <option value="Holden">Holden</option>
                                    <option value="Honda">Honda</option>
                                    <option value="HOWO">HOWO</option>
                                    <option value="Hummer">Hummer</option>
                                    <option value="Hymer">Hymer</option>
                                    <option value="Hyundai">Hyundai</option>
                                    <option value="Hyundai Trucks">Hyundai Trucks</option>
                                    <option value="Immo">Immo</option>
                                    <option value="Infiniti">Infiniti</option>
                                    <option value="International">International</option>
                                    <option value="Isuzu">Isuzu</option>
                                    <option value="Isuzu Truck">Isuzu Truck</option>
                                    <option value="Iveco">Iveco</option>
                                    <option value="Iveco Trucks">Iveco Trucks</option>
                                    <option value="Jac">Jac</option>
                                    <option value="Jac Refine">Jac Refine</option>
                                    <option value="Jaguar">Jaguar</option>
                                    <option value="Jcb">Jcb</option>
                                    <option value="Jeep">Jeep</option>
                                    <option value="Jiangling">Jiangling</option>
                                    <option value="JMC">JMC</option>
                                    <option value="John Deere">John Deere</option>
                                    <option value="Joy Long">Joy Long</option>
                                    <option value="Kamaz">Kamaz</option>
                                    <option value="Kartcross">Kartcross</option>
                                    <option value="Kawasaki">Kawasaki</option>
                                    <option value="Kawasaki Marine">Kawasaki Marine</option>
                                    <option value="Kenworth">Kenworth</option>
                                    <option value="Kia">Kia</option>
                                    <option value="King Long">King Long</option>
                                    <option value="Kioti">Kioti</option>
                                    <option value="Komatsu">Komatsu</option>
                                    <option value="Krone">Krone</option>
                                    <option value="KTM">KTM</option>
                                    <option value="Lambordini">Lambordini</option>
                                    <option value="Lamborghini">Lamborghini</option>
                                    <option value="Lancia">Lancia</option>
                                    <option value="Land Rover">Land Rover</option>
                                    <option value="Landini">Landini</option>
                                    <option value="Laverda">Laverda</option>
                                    <option value="LDV">LDV</option>
                                    <option value="Lexus">Lexus</option>
                                    <option value="Ligier">Ligier</option>
                                    <option value="Lincoln">Lincoln</option>
                                    <option value="Linder">Linder</option>
                                    <option value="London">London</option>
                                    <option value="Lotus">Lotus</option>
                                    <option value="Luxgen">Luxgen</option>
                                    <option value="Macdon">Macdon</option>
                                    <option value="Mack">Mack</option>
                                    <option value="Mahindra">Mahindra</option>
                                    <option value="MAN">MAN</option>
                                    <option value="Manitou">Manitou</option>
                                    <option value="Maserati">Maserati</option>
                                    <option value="Massey Ferguson">Massey Ferguson</option>
                                    <option value="Maverick">Maverick</option>
                                    <option value="Maxus">Maxus</option>
                                    <option value="Maz">Maz</option>
                                    <option value="Mazda">Mazda</option>
                                    <option value="Mccormick">Mccormick</option>
                                    <option value="McLaren">McLaren</option>
                                    <option value="Mercedes Truck">Mercedes Truck</option>
                                    <option value="Mercury">Mercury</option>
                                    <option value="Mercury Marine">Mercury Marine</option>
                                    <option value="Merlo">Merlo</option>
                                    <option value="MG">MG</option>
                                    <option value="Miler">Miler</option>
                                    <option value="Miller">Miller</option>
                                    <option value="Mini">Mini</option>
                                    <option value="Mitsubishi">Mitsubishi</option>
                                    <option value="Morgan">Morgan</option>
                                    <option value="MTZ">MTZ</option>
                                    <option value="MV Augusta">MV Augusta</option>
                                    <option value="Neoplan">Neoplan</option>
                                    <option value="New Holland">New Holland</option>
                                    <option value="Nissan">Nissan</option>
                                    <option value="Otokar">Otokar</option>
                                    <option value="Pellenc">Pellenc</option>
                                    <option value="Peterbilt">Peterbilt</option>
                                    <option value="Pgo">Pgo</option>
                                    <option value="Piaggio">Piaggio</option>
                                    <option value="Polaris">Polaris</option>
                                    <option value="Ponsse">Ponsse</option>
                                    <option value="Porsche">Porsche</option>
                                    <option value="Proton">Proton</option>
                                    <option value="Renault Trucks">Renault Trucks</option>
                                    <option value="RIB">RIB</option>
                                    <option value="Roewe">Roewe</option>
                                    <option value="Rolls Royce">Rolls Royce</option>
                                    <option value="Rostselmash">Rostselmash</option>
                                    <option value="Rover">Rover</option>
                                    <option value="Saab">Saab</option>
                                    <option value="Same">Same</option>
                                    <option value="Santana">Santana</option>
                                    <option value="Scania">Scania</option>
                                    <option value="Seadoo">Seadoo</option>
                                    <option value="Sennebogen">Sennebogen</option>
                                    <option value="SERIE 8">SERIE 8</option>
                                    <option value="Shelby">Shelby</option>
                                    <option value="SINOTRUK">SINOTRUK</option>
                                    <option value="Ski-doo">Ski-doo</option>
                                    <option value="Smart">Smart</option>
                                    <option value="SMF">SMF</option>
                                    <option value="Solaris">Solaris</option>
                                    <option value="Ssang Yong">Ssang Yong</option>
                                    <option value="Sterling">Sterling</option>
                                    <option value="Steyr">Steyr</option>
                                    <option value="Subaru">Subaru</option>
                                    <option value="Suzuki">Suzuki</option>
                                    <option value="Suzuki Marine">Suzuki Marine</option>
                                    <option value="Tata">Tata</option>
                                    <option value="Tata Trucks">Tata Trucks</option>
                                    <option value="Tigercat">Tigercat</option>
                                    <option value="Toyota">Toyota</option>
                                    <option value="TRIUMPH">TRIUMPH</option>
                                    <option value="Troller">Troller</option>
                                    <option value="Trumpchi">Trumpchi</option>
                                    <option value="UAZ">UAZ</option>
                                    <option value="UD">UD</option>
                                    <option value="Valtra">Valtra</option>
                                    <option value="Vas">Vas</option>
                                    <option value="Versitile">Versitile</option>
                                    <option value="VM Motori">VM Motori</option>
                                    <option value="Volkswagen Trucks">Volkswagen Trucks</option>
                                    <option value="Volvo Industrial">Volvo Industrial</option>
                                    <option value="Volvo Marine">Volvo Marine</option>
                                    <option value="Volvo Trucks">Volvo Trucks</option>
                                    <option value="Weichai">Weichai</option>
                                    <option value="XCMG">XCMG</option>
                                    <option value="Yamaha">Yamaha</option>
                                    <option value="Yanmar">Yanmar</option>
                                    <option value="Yuchai">Yuchai</option>
                                    <option value="Yutong">Yutong</option>
                                    <option value="Zastava">Zastava</option>
                                    <option value="Zetor">Zetor</option>
                                    <option value="Zhonghua">Zhonghua</option>
                                    <option value="Zhongtai">Zhongtai</option>
                                    {/* Add more brand options here */}
                                </select>
                            </div>

                            {/* ECU Model Select */}
                            <div>
                                <label
                                    htmlFor="model"
                                    className="block text-xs font-bold uppercase tracking-[0.08em] text-gray-500 dark:text-slate-400"
                                >
                                    ECU Model
                                </label>
                                <select
                                    id="model"
                                    name="model"
                                    className="mt-1.5 block h-11 w-full rounded-[12px] border border-gray-300 bg-white px-3 text-sm text-gray-800 outline-none transition focus:border-red-400 focus:ring-4 focus:ring-red-500/10 disabled:opacity-60 dark:border-[#3a3a3d] dark:bg-[#262628] dark:text-slate-100"
                                    disabled={!formEnabled}
                                    required
                                >
                                    <option value=""></option>
                                    <option value="Bosch">Bosch</option>
                                    <option value="Continental">Continental</option>
                                    <option value="Delco">Delco</option>
                                    <option value="Delphi">Delphi</option>
                                    <option value="Denso">Denso</option>
                                    <option value="Deutz">Deutz</option>
                                    <option value="FoMoCo">FoMoCo</option>
                                    <option value="Heizman">Heizman</option>
                                    <option value="Hella">Hella</option>
                                    <option value="Hitachi">Hitachi</option>
                                    <option value="Honda">Honda</option>
                                    <option value="Isuzu">Isuzu</option>
                                    <option value="Johnson">Johnson</option>
                                    <option value="Kefico">Kefico</option>
                                    <option value="Keihin">Keihin</option>
                                    <option value="Lotus">Lotus</option>
                                    <option value="Lucas">Lucas</option>
                                    <option value="Marelli">Marelli</option>
                                    <option value="Matsushita">Matsushita</option>
                                    <option value="Melco">Melco</option>
                                    <option value="Mitsubishi">Mitsubishi</option>
                                    <option value="Motorola">Motorola</option>
                                    <option value="NGC">NGC</option>
                                    <option value="Perkins">Perkins</option>
                                    <option value="Phoenix">Phoenix</option>
                                    <option value="Saab">Saab</option>
                                    <option value="Sagem">Sagem</option>
                                    <option value="Siemens">Siemens</option>
                                    <option value="Temic">Temic</option>
                                    <option value="Transtron">Transtron</option>
                                    <option value="Trionic">Trionic</option>
                                    <option value="TRW">TRW</option>
                                    <option value="Valeo">Valeo</option>
                                    <option value="VDO">VDO</option>
                                    <option value="Visteon">Visteon</option>
                                    <option value="Zexel">Zexel</option>
                                    <option value="ZF">ZF</option>
                                    <option value="Other">Other</option>
                                    {/* Add more ECU model options here */}
                                </select>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                            {/* ECU Information */}
                            <div>
                                <label
                                    htmlFor="ecu"
                                    className="block text-xs font-bold uppercase tracking-[0.08em] text-gray-500 dark:text-slate-400"
                                >
                                    ECU Information
                                </label>
                                <input
                                    type="text"
                                    id="ecu"
                                    name="ecu"
                                    className="mt-1.5 block h-11 w-full rounded-[12px] border border-gray-300 bg-white px-3 text-sm text-gray-800 outline-none transition focus:border-red-400 focus:ring-4 focus:ring-red-500/10 disabled:opacity-60 dark:border-[#3a3a3d] dark:bg-[#262628] dark:text-slate-100"
                                    placeholder="EDC16, EDC17, etc..."
                                    disabled={!formEnabled}
                                    onChange={updateSolutionPricesByAdditionalPrices}
                                    required
                                />
                            </div>

                            {/* Petrol Type */}
                            <div>
                                <label
                                    htmlFor="petrol_type"
                                    className="block text-xs font-bold uppercase tracking-[0.08em] text-gray-500 dark:text-slate-400"
                                >
                                    Petrol Type
                                </label>
                                <select
                                    id="petrol_type"
                                    name="petrol_type"
                                    className="mt-1.5 block h-11 w-full rounded-[12px] border border-gray-300 bg-white px-3 text-sm text-gray-800 outline-none transition focus:border-red-400 focus:ring-4 focus:ring-red-500/10 disabled:opacity-60 dark:border-[#3a3a3d] dark:bg-[#262628] dark:text-slate-100"
                                    disabled={!formEnabled}
                                    required
                                >
                                    <option value=""></option>
                                    <option value="DIESEL">Diesel</option>
                                    <option value="GASOLINE">Gasoline</option>
                                </select>
                            </div>

                            {/* Year */}
                            <div>
                                <label
                                    htmlFor="generation"
                                    className="block text-xs font-bold uppercase tracking-[0.08em] text-gray-500 dark:text-slate-400"
                                >
                                    Year
                                </label>
                                <select
                                    id="generation"
                                    name="generation"
                                    className="mt-1.5 block h-11 w-full rounded-[12px] border border-gray-300 bg-white px-3 text-sm text-gray-800 outline-none transition focus:border-red-400 focus:ring-4 focus:ring-red-500/10 disabled:opacity-60 dark:border-[#3a3a3d] dark:bg-[#262628] dark:text-slate-100"
                                    disabled={!formEnabled}
                                    required
                                >
                                    <option value=""></option>
                                    <option value="2026">2026</option>
                                    <option value="2025">2025</option>
                                    <option value="2024">2024</option>
                                    <option value="2023">2023</option>
                                    <option value="2022">2022</option>
                                    <option value="2021">2021</option>
                                    <option value="2020">2020</option>
                                    <option value="2019">2019</option>
                                    <option value="2018">2018</option>
                                    <option value="2017">2017</option>
                                    <option value="2016">2016</option>
                                    <option value="2015">2015</option>
                                    <option value="2014">2014</option>
                                    <option value="2013">2013</option>
                                    <option value="2012">2012</option>
                                    <option value="2011">2011</option>
                                    <option value="2010">2010</option>
                                    <option value="2009">2009</option>
                                    <option value="2008">2008</option>
                                    <option value="2007">2007</option>
                                    <option value="2006">2006</option>
                                    <option value="2005">2005</option>
                                    <option value="2004">2004</option>
                                    <option value="2003">2003</option>
                                    <option value="2002">2002</option>
                                    <option value="2001">2001</option>
                                    <option value="2000">2000</option>
                                    {/* Add more year options here */}
                                </select>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                            {/* Gear */}
                            <div>
                                <label
                                    htmlFor="gear"
                                    className="block text-xs font-bold uppercase tracking-[0.08em] text-gray-500 dark:text-slate-400"
                                >
                                    Gear
                                </label>
                                <select
                                    id="gear"
                                    name="gear"
                                    className="mt-1.5 block h-11 w-full rounded-[12px] border border-gray-300 bg-white px-3 text-sm text-gray-800 outline-none transition focus:border-red-400 focus:ring-4 focus:ring-red-500/10 disabled:opacity-60 dark:border-[#3a3a3d] dark:bg-[#262628] dark:text-slate-100"
                                    disabled={!formEnabled}
                                >
                                    <option value=""></option>
                                    <option value="Manual">Manual</option>
                                    <option value="Automatic">Automatic</option>
                                </select>
                            </div>

                            {/* Reading Device */}
                            <div>
                                <label
                                    htmlFor="reading_device"
                                    className="block text-xs font-bold uppercase tracking-[0.08em] text-gray-500 dark:text-slate-400"
                                >
                                    Reading Device
                                </label>
                                <select
                                    id="reading_device"
                                    name="reading_device"
                                    className="mt-1.5 block h-11 w-full rounded-[12px] border border-gray-300 bg-white px-3 text-sm text-gray-800 outline-none transition focus:border-red-400 focus:ring-4 focus:ring-red-500/10 disabled:opacity-60 dark:border-[#3a3a3d] dark:bg-[#262628] dark:text-slate-100"
                                    disabled={!formEnabled}
                                >
                                    <option value=""></option>
                                    <option value="Bitbox">Bitbox</option>
                                    <option value="Kt200">Kt200</option>
                                    <option value="Byteshooter">Byteshooter</option>
                                    <option value="Piasini">Piasini</option>
                                    <option value="Frieling SPI Wizard">
                                        Frieling SPI Wizard
                                    </option>
                                    <option value="Alientech">Alientech</option>
                                    <option value="Dimsport Genius">Dimsport Genius</option>
                                    <option value="PEmicro">PEmicro</option>
                                    <option value="MPPS">MPPS</option>
                                    <option value="Magic Motorsport">Magic Motorsport</option>
                                    <option value="EVC">EVC</option>
                                    <option value="Frieling i-Flash">Frieling i-Flash</option>
                                    <option value="Frieling i-Boot">Frieling i-Boot</option>
                                    <option value="Dimsport New Trasdata">
                                        Dimsport New Trasdata
                                    </option>
                                    <option value="Autotuner">Autotuner</option>
                                    <option value="PCM-Flash">PCM-Flash</option>
                                    <option value="Femto">Femto</option>
                                    <option value="bFlash">bFlash</option>
                                    <option value="CMD">CMD</option>
                                    <option value="DFOX">DFOX</option>
                                    {/* Add more reading devices here */}
                                </select>
                            </div>

                            {/* description */}
                            <div>
                                <label
                                    htmlFor="description"
                                    className="block text-xs font-bold uppercase tracking-[0.08em] text-gray-500 dark:text-slate-400"
                                >
                                    Description
                                </label>
                                <input
                                    id="description"
                                    name="description"
                                    placeholder="Write DTC & Description"
                                    className="mt-1.5 block h-11 w-full rounded-[12px] border border-gray-300 bg-white px-3 text-sm text-gray-800 outline-none transition focus:border-red-400 focus:ring-4 focus:ring-red-500/10 disabled:opacity-60 dark:border-[#3a3a3d] dark:bg-[#262628] dark:text-slate-100"
                                    disabled={!formEnabled}
                                />
                            </div>
                        </div>

                        {/* Choose Solution & DTC Button */}
                        {/*<div className="mt-4">*/}
                        {/*  <button*/}
                        {/*    type="button"*/}
                        {/*    className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"*/}
                        {/*    onClick={() => setShowModal(true)}*/}
                        {/*    disabled={!formEnabled}*/}
                        {/*  >*/}
                        {/*    Choose Solution & DTC*/}
                        {/*  </button>*/}
                        {/*</div>*/}

                        {/*{showModal && (*/}
                        {/*  <ChooseSolution*/}
                        {/*    solutions={solutions}*/}
                        {/*    setShowModal={setShowModal}*/}
                        {/*    setSelectedSolutionIds={setSelectedSolutionIds}*/}
                        {/*    selectedSolutionIds={selectedSolutionIds}*/}
                        {/*    setTotalCredits={setTotalCredits}*/}
                        {/*    selected={selected}*/}
                        {/*    setSelected={setSelected}*/}
                        {/*    setSelectedOptions={setSelectedOptions}*/}
                        {/*    selectedOptions={selectedOptions}*/}
                        {/*    limitExceeded={limitExceeded}*/}
                        {/*    setLimitExceeded={setLimitExceeded}*/}
                        {/*  />*/}
                        {/*)}*/}

                        <ChooseSolutionV2
                            solutions={solutions}
                            setShowModal={setShowModal}
                            setSelectedSolutionIds={setSelectedSolutionIds}
                            selectedSolutionIds={selectedSolutionIds}
                            setTotalCredits={setTotalCredits}
                            selected={selected}
                            setSelected={setSelected}
                            setSelectedOptions={setSelectedOptions}
                            selectedOptions={selectedOptions}
                            limitExceeded={limitExceeded}
                            setLimitExceeded={setLimitExceeded}
                            setSolutionErrorMessage={setSolutionErrorMessage}
                        />

                        {solutionErrorMessage && <ErrorAlert errorMessage={solutionErrorMessage}/>}

                        <div className="mt-6">
              <textarea
                  className="mt-1.5 block h-11 w-full rounded-[12px] border border-gray-300 bg-white px-3 text-sm text-gray-800 outline-none transition focus:border-red-400 focus:ring-4 focus:ring-red-500/10 disabled:opacity-60 dark:border-[#3a3a3d] dark:bg-[#262628] dark:text-slate-100"
                  rows="4"
                  placeholder="Selected options will appear here..."
                  value={selectedOptions.join("\n")}
                  readOnly
                  required
              />
                        </div>

                        {/* Submit Button */}
                        <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
                            <button
                                type="submit"
                                className={buttonClass()}
                                disabled={!file || isLoading}
                            >
                                {isLoading ? <Spinner/> : <ArrowUpTrayIcon className="h-5 w-5"/>}
                                {isLoading ? "Uploading…" : "Upload"}
                            </button>

                            <div className="flex h-11 items-center gap-2 rounded-full border border-gray-300 px-4 dark:border-[#3a3a3d]">
                                <span className="text-xs font-bold uppercase tracking-[0.08em] text-gray-400 dark:text-slate-500">
                                    Total
                                </span>
                                <span className="bg-gradient-to-r from-red-600 to-orange-500 bg-clip-text text-lg font-extrabold text-transparent">
                                    {totalCredits}
                                </span>
                                <span className="text-xs font-semibold text-gray-400 dark:text-slate-500">CRS</span>
                            </div>
                        </div>
                    </div>
                </form>
                {successModal && <SuccessModal isModal={true}/>}
                {errorModal && (
                    <ErrorModal
                        errorMessage={
                            "There was an error uploading your file. Please try again."
                        }
                    />
                )}
            </Panel>

            {isLoading && <ProgressBar progress={progress}/>}
        </Page>
    );
};

export default FileUpload;
